<template>
	<section class="v2c">

		<!-- ════════ NO DEVICES STATE ════════ -->
		<div v-if="!stats.specs || !stats.specs.length" class="v2c__empty">
			<div class="v2c__empty-icon">
				<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/></svg>
			</div>
			<p class="v2c__empty-title">{{ $t('campaign.step04_no_devices') }}</p>
			<p class="v2c__empty-desc">Go back to Targeting step and select devices to see available creative formats.</p>
		</div>

		<!-- ════════ SPECS BANNER ════════ -->
		<div v-if="stats.specs && stats.specs.length" class="v2c__specs-banner">
			<div class="v2c__specs-icon">
				<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
			</div>

			<p class="v2c__specs-text">
				{{ $t('campaign.creative_text') }}
				<a
					v-if="$uioverrides.creative_specification_file"
					:href="$uioverrides.creative_specification_file"
					class="v2c__link"
					target="_blank"
				>{{ $t('campaign.creative_full_spec_btn_label') }} &rarr;</a>
				<a
					v-else
					class="v2c__link"
					@click="isCreativeSpecShown = true"
				>{{ $t('campaign.creative_full_spec_btn_label') }} &rarr;</a>
			</p>
		</div>

		<!-- ════════ FORMAT GROUPS ════════ -->
		<div
			v-for="format in creativeList"
			:key="`${format.type}-${format.resolution}`"
			class="v2c__format-group"
		>
			<!-- Format Header -->
			<div class="v2c__format-header">
				<div
					class="v2c__format-icon"
					:class="{ 'v2c__format-icon--audio': format.type === 'audio' }"
				>
					<!-- Monitor icon -->
					<svg v-if="format.type === 'videoOrImage'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/></svg>
					<!-- Audio icon -->
					<svg v-else-if="format.type === 'audio'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
					<!-- Other icon -->
					<svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>
				</div>

				<div class="v2c__format-info">
					<p class="v2c__format-title">
						<template v-if="format.type === 'videoOrImage'">
							{{ $t('campaign.monitor') | capitalize }} &middot; {{ format.resolution }} &middot;
							{{ +format.resolution.split('x')[0] >= +format.resolution.split('x')[1] ? $t('common.horizontal') : $t('common.vertical') }}
						</template>
						<template v-else-if="format.type === 'audio'">
							{{ $t('campaign.audio') | capitalize }}
						</template>
						<template v-else>
							{{ $t('campaign.other') | capitalize }}
						</template>
					</p>
					<p v-if="format.count" class="v2c__format-sub">{{ $tc('campaign.devices', format.count) }} matching this format</p>
				</div>

				<span
					class="v2c__format-badge"
					:class="format.ads && format.ads.length ? 'v2c__format-badge--has' : 'v2c__format-badge--empty'"
				>
					{{ format.ads && format.ads.length ? format.ads.length + ' creative' + (format.ads.length > 1 ? 's' : '') : 'No creatives' }}
				</span>
			</div>

			<!-- Format Body -->
			<div class="v2c__format-body">

				<!-- Uploaded creatives -->
				<template v-if="format.ads && format.ads.length">
					<div
						v-for="(ad, index) in format.ads"
						:key="`${ad.id}`"
						class="v2c__creative-wrap"
					>
						<creative-preview
							v-if="ad.video !== null"
							:format="format"
							:creative="format.ads[index]"
							:context="context"
							class="v2c__creative-preview"
							auditable
							@remove="remove"
						/>

						<!-- Attachments -->
						<ul v-if="ad.attachments && ad.attachments.length" class="v2c__attachments">
							<li v-for="attachment in sortedAttachments(ad.attachments)" :key="attachment.id">
								<a :href="attachment.file" target="_blank" class="v2c__link">{{ attachment.file_name }}</a>
								<span class="v2c__attachment-delete" @click="removeAttachment(ad.id, attachment.id)">
									<nice-icon-2 icon="basket-14" />
								</span>
							</li>
						</ul>

						<!-- Upload buttons for existing ad without video -->
						<template v-if="format.type != 'other'">
							<!-- creative MUST be uploaded before document -->
							<!-- <AddCreative
								v-if="ad.video == null"
								:adId="ad.id"
								:context="context"
								:format="format"
								:disabled="isUploading(ad.id)"
								clean-on-upload
								@update:creativeData="pushCreative"
								@upload:start="setUploading(ad.id, true)"
								@upload:end="setUploading(ad.id, false)"
							/> -->
							<AddDocument
								:adId="ad.id"
								:campaignId="context.instance.id"
								:group="`${format.type}-${format.resolution}`"
								:disabled="isUploading(ad.id)"
								@update:creativeData="pushCreative"
								@upload:start="setUploading(ad.id, true, false)"
								@upload:end="setUploading(ad.id, false, $event)"
							/>
							<nice-hint
								v-if="getUploadError(ad.id)"
								icon="error"
								class="v2c__upload-error"
								:maxWidth="200"
							>{{ getUploadError(ad.id) }}</nice-hint>
						</template>
					</div>
				</template>

				<!-- Upload buttons: guaranteed campaigns allow several creatives for a single format -->
				<div class="v2c__add-creative-block">
					<AddCreative
						:context="context"
						:format="format"
						:disabled="isUploading(getGroup(format))"
						clean-on-upload
						@update:creativeData="pushCreative"
						@upload:start="setUploading(getGroup(format), true)"
						@upload:end="setUploading(getGroup(format), false)"
					/>
					<!-- Don't show add document button before create is uploaded -->
					<!-- <AddDocument
						:campaignId="context.instance.id"
						:group="getGroup(format)"
						:disabled="isUploading(getGroup(format))"
						@update:creativeData="pushCreative"
						@upload:start="setUploading(getGroup(format), true, false)"
						@upload:end="setUploading(getGroup(format), false, $event)"
					/> -->
					<nice-hint
						v-if="getUploadError(getGroup(format))"
						icon="error"
						class="v2c__upload-error"
						:maxWidth="200"
					>{{ getUploadError(getGroup(format)) }}</nice-hint>
				</div>

			</div>
		</div>

		<!-- ════════ POLICY FOOTER ════════ -->
		<div class="v2c__policy-footer">
			<p class="v2c__policy-text">
				{{ $t('campaign.privacy_policies_line_01') | capitalize }}
				<a
					v-if="$uioverrides.regulatory_docs_file"
					:href="$uioverrides.regulatory_docs_file"
					class="v2c__link"
					target="_blank"
				>{{ $t('campaign.privacy_policies_line_02') }}</a>
				<a
					v-else
					class="v2c__link"
					@click="isCreativePolicyShown = true"
				>{{ $t('campaign.privacy_policies_line_02') }}</a>
				{{ $t('campaign.privacy_policies_line_03') }}
			</p>
		</div>

		<!-- ════════ VALIDATION HINTS ════════ -->
		<div v-if="areAdsNotValid || !isEmailVerified || isInsufficientBalance" class="v2c__validation">
			<template v-if="areAdsNotValid && areAdsRequiredForApproval">
				<span class="v2c__validation-msg v2c__validation-msg--error">{{ $t('campaign.no_creatives_issue') }}</span>
			</template>
			<template v-else-if="!isEmailVerified">
				<span class="v2c__validation-msg v2c__validation-msg--error">{{ $t('campaign.confirm_email') }}</span>
			</template>
			<template v-else-if="isInsufficientBalance">
				<span class="v2c__validation-msg v2c__validation-msg--error">{{ $t('campaign.insufficient_balance_request_dsp_view') }}</span>
			</template>
			<template v-else-if="areAdsNotValid">
				<span class="v2c__validation-msg v2c__validation-msg--warn">{{ $t('campaign.no_creatives_warning') }}</span>
			</template>
		</div>

		<!-- ════════ MODALS ════════ -->
		<CreativeSpecification
			v-if="isCreativeSpecShown"
			@close="isCreativeSpecShown = false"
		/>
		<CreativePolicy
			v-if="isCreativePolicyShown"
			@close="isCreativePolicyShown = false"
		/>

	</section>
</template>


<script>
import Step04 from '@/ui-overrides/default/pages/request/create/step_04.vue';

export default {
	name: 'V2StepCreatives',

	extends: Step04,

	components: {
		...Step04.components,
	},
};
</script>


<style lang="sass" scoped>
@import '~@/assets/styles/v2-tokens.sass'

$teal: $v2-teal
$purple: $v2-purple
$border: $v2-border
$card: $v2-card-bg
$text: $v2-text
$muted: $v2-text-muted
$bg: #f3f4f6
$font: $v2-font
$r-card: 16px
$r-input: 12px
$shadow: $v2-shadow-card

// ── Root ───────────────────────────
.v2c
	display: flex
	flex-direction: column
	gap: 20px
	font-family: $font

// ── Empty State ────────────────────
.v2c__empty
	border-radius: $r-card
	border: 2px dashed $border
	padding: 40px
	text-align: center

.v2c__empty-icon
	margin: 0 auto 16px
	width: 64px
	height: 64px
	display: flex
	align-items: center
	justify-content: center
	border-radius: $r-card
	background: rgba($bg, 0.5)
	color: $muted

.v2c__empty-title
	font-size: 14px
	font-weight: 600
	margin-bottom: 4px

.v2c__empty-desc
	font-size: 12px
	color: $muted
	max-width: 360px
	margin: 0 auto

// ── Specs Banner ───────────────────
.v2c__specs-banner
	display: flex
	align-items: flex-start
	gap: 12px
	border-radius: $r-card
	border: 1px solid rgba($teal, 0.15)
	background: linear-gradient(to right, rgba($teal, 0.05), transparent)
	padding: 16px

.v2c__specs-icon
	width: 36px
	height: 36px
	flex-shrink: 0
	border-radius: 12px
	background: rgba($teal, 0.1)
	color: $teal
	display: flex
	align-items: center
	justify-content: center

.v2c__specs-text
	font-size: 12px
	line-height: 1.6
	color: $muted

// ── Links ──────────────────────────
.v2c__link
	font-weight: 600
	color: $teal
	text-decoration: none
	cursor: pointer

	&:hover
		text-decoration: underline

// ── Format Group ───────────────────
.v2c__format-group
	overflow: hidden
	border-radius: $r-card
	border: 1px solid rgba($border, 0.6)
	background: $card
	box-shadow: $shadow

.v2c__format-header
	display: flex
	align-items: center
	gap: 12px
	border-bottom: 1px solid rgba($border, 0.4)
	background: rgba($bg, 0.3)
	padding: 16px 20px

.v2c__format-icon
	width: 36px
	height: 36px
	flex-shrink: 0
	border-radius: 12px
	background: rgba($teal, 0.1)
	color: $teal
	display: flex
	align-items: center
	justify-content: center

	&--audio
		background: rgba($purple, 0.1)
		color: $purple

.v2c__format-info
	flex: 1
	min-width: 0

.v2c__format-title
	font-size: 13px
	font-weight: 600
	color: $text

.v2c__format-sub
	font-size: 11px
	color: $muted

.v2c__format-badge
	border-radius: 9999px
	padding: 2px 10px
	font-size: 10px
	font-weight: 600
	text-transform: uppercase
	letter-spacing: 0.05em

	&--has
		background: rgba(#22c55e, 0.1)
		color: #22c55e

	&--empty
		background: $bg
		color: $muted

.v2c__format-body
	padding: 20px
	display: flex
	flex-direction: column
	gap: 12px

// ── Creative Preview Override ──────
.v2c__creative-preview
	::v-deep
		border-radius: $r-input
		border: 1px solid rgba($border, 0.4)
		padding: 16px
		transition: box-shadow 0.2s

		&:hover
			box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1)

.v2c__creative-wrap
	display: flex
	flex-direction: column
	gap: 8px

.v2c__add-creative-block
	margin-top: 20px

	&:first-child
		margin-top: 0

// ── Attachments ────────────────────
.v2c__attachments
	margin: 0
	padding: 0
	list-style: none

	li
		display: flex
		align-items: center
		gap: 4px
		font-size: 12px
		margin-bottom: 4px

.v2c__attachment-delete
	cursor: pointer
	color: $muted

	&:hover
		color: $v2-red

// ── Upload Error ───────────────────
.v2c__upload-error
	margin-top: 4px

// ── Policy Footer ──────────────────
.v2c__policy-footer
	border-radius: $r-card
	border: 1px solid rgba($border, 0.6)
	background: $card
	padding: 16px
	box-shadow: $shadow

.v2c__policy-text
	font-size: 12px
	color: $muted

// ── Validation ─────────────────────
.v2c__validation
	padding: 12px 16px
	border-radius: $r-input
	font-size: 12px

.v2c__validation-msg
	font-weight: 500

	&--error
		color: $v2-red

	&--warn
		color: $v2-orange
</style>
